<script setup lang="ts">
// OnboardingWizard — modal overlay shown on first login.
// 5 steps: Welcome → Create Instance → Connect WhatsApp (QR) → Send Test → Success
// Persists completion in localStorage as 'neogo_onboarding_done'.
import { ref, computed, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import { useUserStore } from '../stores/user'
import {
  createInstance,
  connectInstance,
  getInstanceInfo,
  sendText,
} from '../lib/api'
import type { Instance } from '../lib/types'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import ProgressBar from 'primevue/progressbar'

const emit = defineEmits<{ complete: [] }>()

const { t } = useI18n()
const router = useRouter()
const toast = useToast()
const userStore = useUserStore()

const visible = ref(true)
const activeStep = ref(0)
const totalSteps = 5

// Step 2 — create instance
const instanceName = ref('')
const creating = ref(false)
const createdInstance = ref<Instance | null>(null)

// Step 3 — QR code + connect
const qrBase64 = ref('')
const qrPairingCode = ref('')
const qrPolling = ref(false)
const connected = ref(false)
let pollTimer: ReturnType<typeof setTimeout> | null = null

// Step 4 — send test
const testNumber = ref('')
const testMessage = ref(t('access.onboarding.wizard.messageDefault'))
const sending = ref(false)
const sentOk = ref(false)

const progressPercent = computed(() => Math.round(((activeStep.value + 1) / totalSteps) * 100))

function generateToken(): string {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

function completeOnboarding() {
  localStorage.setItem('neogo_onboarding_done', 'true')
  visible.value = false
  emit('complete')
}

function skip() {
  completeOnboarding()
}

// --- Step 2: Create Instance ---
async function handleCreateInstance() {
  if (!instanceName.value.trim()) return
  creating.value = true
  const token = generateToken()
  try {
    const res = await createInstance({
      name: instanceName.value.trim(),
      token,
      organizationId: userStore.user?.organizationId,
    })
    createdInstance.value = res.data?.data ?? res.data
    // Ensure we have the token (may not come back from API)
    if (createdInstance.value && !createdInstance.value.token) {
      createdInstance.value.token = token
    }
    toast.add({ severity: 'success', summary: t('access.onboarding.wizard.toasts.successTitle'), detail: t('access.onboarding.wizard.toasts.createOk'), life: 3000 })
    activeStep.value = 2
    // Auto-start connection + QR polling
    await startConnection()
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('access.onboarding.wizard.toasts.createFailedDefault')
    toast.add({ severity: 'error', summary: t('common.error'), detail: msg, life: 4000 })
  } finally {
    creating.value = false
  }
}

// --- Step 3: Connect via QR ---
async function startConnection() {
  if (!createdInstance.value?.token) return
  try {
    await connectInstance(createdInstance.value.token)
  } catch {
    // May already be in connecting state — continue to QR polling
  }
  startQrPolling()
}

function startQrPolling() {
  if (!createdInstance.value?.id) return
  qrPolling.value = true
  qrBase64.value = ''
  qrPairingCode.value = ''
  connected.value = false
  let attempts = 0
  const maxAttempts = 60 // 2 minutes at 2s intervals

  const poll = async () => {
    if (!visible.value || connected.value || attempts >= maxAttempts) {
      qrPolling.value = false
      return
    }
    attempts++
    try {
      const infoRes = await getInstanceInfo(createdInstance.value!.id)
      const info = infoRes.data?.data || infoRes.data
      if (info?.connected) {
        connected.value = true
        qrPolling.value = false
        toast.add({ severity: 'success', summary: t('access.onboarding.toasts.connected'), detail: t('access.onboarding.wizard.toasts.connectedDetail'), life: 3000 })
        setTimeout(() => { activeStep.value = 3 }, 800)
        return
      }
      if (info?.qrcode && info.qrcode.length > 10) {
        const raw = info.qrcode
        const pipeIdx = raw.indexOf('|')
        const imgData = pipeIdx > 0 ? raw.substring(0, pipeIdx) : raw
        qrBase64.value = imgData.startsWith('data:image') ? imgData : `data:image/png;base64,${imgData}`
        if (pipeIdx > 0) {
          const match = raw.substring(pipeIdx + 1).match(/^(\d[\d-]+\d)/)
          if (match) qrPairingCode.value = match[1]
        }
      }
    } catch {
      // Instance info not ready yet — keep polling
    }
    pollTimer = setTimeout(poll, 2000)
  }
  void poll()
}

function stopPolling() {
  if (pollTimer) {
    clearTimeout(pollTimer)
    pollTimer = null
  }
  qrPolling.value = false
}

// --- Step 4: Send test message ---
async function handleSendTest() {
  if (!createdInstance.value?.token || !testNumber.value.trim()) return
  sending.value = true
  sentOk.value = false
  try {
    await sendText(createdInstance.value.token, {
      number: testNumber.value.replace(/\D/g, ''),
      text: testMessage.value || t('access.onboarding.wizard.messageDefault'),
    })
    sentOk.value = true
    toast.add({ severity: 'success', summary: t('access.onboarding.wizard.toasts.sent'), detail: t('access.onboarding.wizard.toasts.sentDetail'), life: 3000 })
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('access.onboarding.wizard.toasts.sendFailedDefault')
    toast.add({ severity: 'error', summary: t('common.error'), detail: msg, life: 4000 })
  } finally {
    sending.value = false
  }
}

function goToDashboard() {
  completeOnboarding()
  router.push('/manager')
}

// Cleanup polling on unmount or dialog close
watch(visible, (val) => {
  if (!val) stopPolling()
})

onUnmounted(() => {
  stopPolling()
})
</script>

<template>
  <Dialog
    v-model:visible="visible"
    :modal="true"
    :closable="false"
    :draggable="false"
    :style="{ width: '580px', maxWidth: '95vw' }"
    class="onboarding-wizard-dialog"
  >
    <template #header>
      <div class="wizard-header">
        <div class="wizard-header-left">
          <i class="pi pi-whatsapp wizard-logo"></i>
          <span class="wizard-title">{{ t('access.onboarding.wizard.header') }}</span>
        </div>
        <Button
          :label="t('access.onboarding.skip')"
          icon="pi pi-times"
          text
          severity="secondary"
          size="small"
          @click="skip"
        />
      </div>
    </template>

    <!-- Progress indicator -->
    <div class="wizard-progress">
      <div class="wizard-progress-label">
        {{ t('access.onboarding.wizard.stepLabel', { current: activeStep + 1, total: totalSteps }) }}
      </div>
      <ProgressBar :value="progressPercent" :showValue="false" class="wizard-progress-bar" />
    </div>

    <!-- Step 0: Welcome -->
    <div v-if="activeStep === 0" class="wizard-step">
      <div class="wizard-step-icon-wrap">
        <i class="pi pi-sparkles wizard-step-icon"></i>
      </div>
      <h3 class="wizard-step-title">{{ t('access.onboarding.wizard.welcomeTitle') }}</h3>
      <p class="wizard-step-desc">{{ t('access.onboarding.wizard.welcomeDesc') }}</p>
      <div class="wizard-step-checklist">
        <div class="wizard-check-item">
          <i class="pi pi-server"></i>
          <span>{{ t('access.onboarding.wizard.checkCreate') }}</span>
        </div>
        <div class="wizard-check-item">
          <i class="pi pi-qrcode"></i>
          <span>{{ t('access.onboarding.wizard.checkQr') }}</span>
        </div>
        <div class="wizard-check-item">
          <i class="pi pi-send"></i>
          <span>{{ t('access.onboarding.wizard.checkSend') }}</span>
        </div>
      </div>
      <div class="wizard-actions">
        <Button
          :label="t('access.onboarding.wizard.begin')"
          icon="pi pi-arrow-right"
          iconPos="right"
          class="wa-primary"
          @click="activeStep = 1"
        />
      </div>
    </div>

    <!-- Step 1: Create Instance -->
    <div v-else-if="activeStep === 1" class="wizard-step">
      <div class="wizard-step-icon-wrap">
        <i class="pi pi-server wizard-step-icon"></i>
      </div>
      <h3 class="wizard-step-title">{{ t('access.onboarding.wizard.createTitle') }}</h3>
      <p class="wizard-step-desc">{{ t('access.onboarding.wizard.createDesc') }}</p>
      <div class="wizard-field">
        <label class="wizard-label" for="onb-instance-name">{{ t('access.onboarding.wizard.instanceNameLabel') }}</label>
        <InputText
          id="onb-instance-name"
          v-model="instanceName"
          :placeholder="t('access.onboarding.instanceNamePlaceholder')"
          class="w-full"
          @keyup.enter="handleCreateInstance"
        />
      </div>
      <div class="wizard-actions">
        <Button
          :label="t('access.onboarding.wizard.back')"
          icon="pi pi-arrow-left"
          text
          severity="secondary"
          @click="activeStep = 0"
        />
        <Button
          :label="t('access.onboarding.wizard.createAndConnect')"
          icon="pi pi-plus"
          iconPos="right"
          class="wa-primary"
          :loading="creating"
          :disabled="!instanceName.trim()"
          @click="handleCreateInstance"
        />
      </div>
    </div>

    <!-- Step 2: Connect WhatsApp (QR Code) -->
    <div v-else-if="activeStep === 2" class="wizard-step">
      <div class="wizard-step-icon-wrap">
        <i class="pi pi-qrcode wizard-step-icon"></i>
      </div>
      <h3 class="wizard-step-title">{{ t('access.onboarding.wizard.connectTitle') }}</h3>

      <div v-if="connected" class="wizard-connected">
        <i class="pi pi-check-circle wizard-connected-icon"></i>
        <div class="wizard-connected-text">{{ t('access.onboarding.wizard.connectSuccess') }}</div>
        <Button
          :label="t('access.onboarding.wizard.continue')"
          icon="pi pi-arrow-right"
          iconPos="right"
          class="wa-primary mt-4"
          @click="activeStep = 3"
        />
      </div>

      <div v-else>
        <p class="wizard-step-desc">
          {{ t('access.onboarding.wizard.openWhatsappPrompt') }}
        </p>
        <ol class="wizard-instructions">
          <li>{{ t('access.onboarding.wizard.stepSettings') }}</li>
          <li>{{ t('access.onboarding.wizard.stepLinkedDevices') }}</li>
          <li>{{ t('access.onboarding.wizard.stepLinkDevice') }}</li>
          <li>{{ t('access.onboarding.wizard.stepScanQr') }}</li>
        </ol>

        <div class="wizard-qr-area">
          <div v-if="qrBase64" class="wizard-qr-container">
            <img :src="qrBase64" alt="QR Code WhatsApp" class="wizard-qr-img" />
          </div>
          <div v-else class="wizard-qr-loading">
            <i class="pi pi-spin pi-spinner" style="font-size: 2rem;"></i>
            <span>{{ t('access.onboarding.wizard.generatingQr') }}</span>
          </div>
        </div>

        <div v-if="qrPairingCode" class="wizard-pairing">
          {{ t('access.onboarding.wizard.pairingCode') }} <strong>{{ qrPairingCode }}</strong>
        </div>

        <p class="wizard-hint">
          <i class="pi pi-info-circle"></i>
          {{ t('access.onboarding.wizard.autoAdvanceHint') }}
        </p>
      </div>

      <div v-if="!connected" class="wizard-actions">
        <Button
          :label="t('access.onboarding.wizard.back')"
          icon="pi pi-arrow-left"
          text
          severity="secondary"
          @click="activeStep = 1; stopPolling()"
        />
      </div>
    </div>

    <!-- Step 3: Send Test Message -->
    <div v-else-if="activeStep === 3" class="wizard-step">
      <div class="wizard-step-icon-wrap">
        <i class="pi pi-send wizard-step-icon"></i>
      </div>
      <h3 class="wizard-step-title">{{ t('access.onboarding.wizard.sendTestTitle') }}</h3>
      <p class="wizard-step-desc">{{ t('access.onboarding.wizard.sendTestDesc') }}</p>
      <div class="wizard-field">
        <label class="wizard-label" for="onb-test-number">{{ t('access.onboarding.wizard.destNumberLabel') }}</label>
        <InputText
          id="onb-test-number"
          v-model="testNumber"
          :placeholder="t('access.onboarding.testNumberPlaceholder')"
          class="w-full"
        />
      </div>
      <div class="wizard-field">
        <label class="wizard-label" for="onb-test-msg">{{ t('access.onboarding.wizard.messageLabel') }}</label>
        <InputText
          id="onb-test-msg"
          v-model="testMessage"
          class="w-full"
        />
      </div>

      <div v-if="sentOk" class="wizard-success-msg">
        <i class="pi pi-check-circle"></i>
        {{ t('access.onboarding.wizard.messageSentInfo') }}
      </div>

      <div class="wizard-actions">
        <Button
          :label="t('access.onboarding.wizard.back')"
          icon="pi pi-arrow-left"
          text
          severity="secondary"
          @click="activeStep = 2"
        />
        <Button
          v-if="!sentOk"
          :label="t('access.onboarding.wizard.sendTestBtn')"
          icon="pi pi-send"
          class="wa-primary"
          :loading="sending"
          :disabled="!testNumber.trim()"
          @click="handleSendTest"
        />
        <Button
          v-if="sentOk"
          :label="t('access.onboarding.wizard.continue')"
          icon="pi pi-arrow-right"
          iconPos="right"
          class="wa-primary"
          @click="activeStep = 4"
        />
      </div>
    </div>

    <!-- Step 4: Success -->
    <div v-else-if="activeStep === 4" class="wizard-step wizard-step-success">
      <div class="wizard-step-icon-wrap wizard-step-icon-success">
        <i class="pi pi-check-circle wizard-step-icon"></i>
      </div>
      <h3 class="wizard-step-title">{{ t('access.onboarding.wizard.doneTitle') }}</h3>
      <p class="wizard-step-desc">{{ t('access.onboarding.wizard.doneDesc') }}</p>
      <div class="wizard-step-checklist wizard-step-checklist-done">
        <div class="wizard-check-item wizard-check-done">
          <i class="pi pi-check"></i>
          <span>{{ t('access.onboarding.wizard.doneCheckCreated') }}</span>
        </div>
        <div class="wizard-check-item wizard-check-done">
          <i class="pi pi-check"></i>
          <span>{{ t('access.onboarding.wizard.doneCheckConnected') }}</span>
        </div>
        <div class="wizard-check-item wizard-check-done">
          <i class="pi pi-check"></i>
          <span>{{ t('access.onboarding.wizard.doneCheckSent') }}</span>
        </div>
      </div>
      <div class="wizard-actions wizard-actions-center">
        <Button
          :label="t('access.onboarding.wizard.goToDashboard')"
          icon="pi pi-home"
          class="wa-primary"
          size="large"
          @click="goToDashboard"
        />
      </div>
    </div>
  </Dialog>
</template>

<style scoped>
.wizard-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.wizard-header-left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.wizard-logo {
  font-size: 1.5rem;
  color: #25D366;
}

.wizard-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--p-text-color);
}

.wizard-progress {
  margin-bottom: 1.5rem;
}

.wizard-progress-label {
  font-size: 0.8rem;
  color: var(--p-text-muted-color);
  margin-bottom: 0.375rem;
}

.wizard-progress-bar {
  height: 6px;
}

.wizard-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0.5rem 0 1rem;
}

.wizard-step-icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: color-mix(in srgb, #25D366 15%, transparent);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
}

.wizard-step-icon {
  font-size: 1.75rem;
  color: #25D366;
}

.wizard-step-icon-success .wizard-step-icon {
  font-size: 2.25rem;
}

.wizard-step-title {
  margin: 0 0 0.5rem;
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--p-text-color);
}

.wizard-step-desc {
  margin: 0 0 1.25rem;
  font-size: 0.938rem;
  line-height: 1.5;
  color: var(--p-text-muted-color);
  max-width: 440px;
}

.wizard-step-checklist {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  margin-bottom: 1.5rem;
  text-align: left;
}

.wizard-check-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-size: 0.938rem;
  color: var(--p-text-color);
}

.wizard-check-item i {
  color: #25D366;
  font-size: 1rem;
}

.wizard-check-done i {
  color: #25D366;
}

.wizard-step-checklist-done .wizard-check-item {
  color: var(--p-text-muted-color);
}

.wizard-field {
  width: 100%;
  max-width: 400px;
  margin-bottom: 1rem;
  text-align: left;
}

.wizard-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--p-text-color);
  margin-bottom: 0.375rem;
}

.wizard-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 400px;
  margin-top: 0.5rem;
  gap: 0.75rem;
}

.wizard-actions-center {
  justify-content: center;
}

.wizard-instructions {
  text-align: left;
  max-width: 400px;
  margin: 0 auto 1.25rem;
  padding-left: 1.25rem;
  color: var(--p-text-muted-color);
  font-size: 0.9rem;
  line-height: 1.8;
}

.wizard-qr-area {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
}

.wizard-qr-container {
  background: #ffffff;
  padding: 1rem;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.wizard-qr-img {
  width: 220px;
  height: 220px;
  display: block;
}

.wizard-qr-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 3rem 2rem;
  color: var(--p-text-muted-color);
}

.wizard-pairing {
  font-size: 0.85rem;
  color: var(--p-text-muted-color);
  margin-bottom: 0.75rem;
}

.wizard-hint {
  font-size: 0.8rem;
  color: var(--p-text-muted-color);
  display: flex;
  align-items: center;
  gap: 0.375rem;
  justify-content: center;
  margin-bottom: 0.5rem;
}

.wizard-connected {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 0;
}

.wizard-connected-icon {
  font-size: 3rem;
  color: #25D366;
  margin-bottom: 0.75rem;
}

.wizard-connected-text {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--p-text-color);
}

.wizard-success-msg {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  background: color-mix(in srgb, #25D366 12%, transparent);
  color: #25D366;
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  width: 100%;
  max-width: 400px;
}

.wa-primary {
  background: #25D366 !important;
  border-color: #25D366 !important;
  color: #ffffff !important;
}

.wa-primary:hover {
  background: #1fb855 !important;
  border-color: #1fb855 !important;
}

.wa-primary:focus {
  box-shadow: 0 0 0 2px color-mix(in srgb, #25D366 30%, transparent) !important;
}
</style>
